// --- SPORTSBOOK & BET TYPE CONSTANTS — no React dependencies -----------------

export const APP_VERSION = "1.0.1908";
export const BUILD_TYPE = "Beta"; // Beta | Prod | Dev

// #1037 ADDED 'nfl'. The paragraph below is kept as the record of what had to be
// true first, because it is the checklist any future league must clear.
//
// It was deliberately absent until #1037 -- see KNOWN_LEAGUES in
// api/lib/league-utils.js. This constant drives the client Realtime filter and
// the wire/roster queries; adding NFL here would make every NFL game_cache row
// (~16, rewritten every minute) push a re-render for a game no surface can
// display, and would flip the app-wide dataSource permanently to "espn",
// destroying the ESPN-fallback triage signal (Hotspot #2).
export const SUPPORTED_LEAGUES = ['nhl', 'mlb', 'nfl'];

// Leagues whose PRIMARY source is ESPN, so an "espn" source string on their rows
// is normal rather than evidence of degradation.
//
// dataSource (useGameCacheRealtime) answers "did we fall back to ESPN?", which is
// the Hotspot #2 triage signal. That question only has meaning for a league with a
// first-party primary: NHL and MLB normally report source 'nhl'/'mlb' and only read
// 'espn' when their primary failed. NFL has no non-ESPN path at all
// (api/nfl-schedule.js), so its rows would pin the signal to "espn" for the whole
// season and destroy it.
//
// Deliberately enumerates the EXCEPTION, not the rule: adding another first-party
// league needs no edit here, only adding another ESPN-primary one does.
// 'nba' (#1655 B3a item 6): api/nba-schedule.js is ESPN-only too, like NFL.
export const ESPN_PRIMARY_LEAGUES = ['nfl', 'nba'];

// Leagues with a SETTLEMENT path -- THE settlement-capability authority (#1040 PR A).
//
// This is not a mirror of anything. api/settle-slips.js, api/lib/settlement-processor.js
// and api/run-data-quality-checks.js CHECK 3/4 import this constant directly (api/
// already imports src/lib in ten files; the reverse is a CRA build error), the same
// shared-module pattern as src/lib/league-authority.js, so client grading and server
// settlement cannot drift -- settleable-leagues-drift.test.js pins the remaining
// invariants (subset of the polling registry; per-league sweep list non-empty).
//
// DELIBERATELY NOT the polling-adapter registry (api/lib/adapters/polling-adapter-
// registry.js). "Can this league be polled for stats?" and "can this league be
// graded?" were the same set until #1040 PR B registered the NFL adapter a PR ahead
// of grading. A league must have an adapter to be settleable (frozen_stat comes from
// polling), so this list is always a SUBSET of the registry -- never derive it from
// the registry.
//
// DELIBERATELY NOT the same list as SUPPORTED_LEAGUES either. A league becomes
// visible and importable (SUPPORTED_LEAGUES) before anything can grade it; the two
// coincide today only because NFL joined here in #1040 PR C.
//
// calcStatus gates on THIS list, not on marketSupportStatus(). That distinction is
// load-bearing: NHL's `points` is UNSUPPORTED_FAIL_CLOSED, yet getPlayerStat()
// resolves it and real vision-imported legs grade on it today -- a market-support
// gate would stop settling live user data (CC-DENY-012). League settleability is the
// question calcStatus actually needs answered: can ANYTHING grade this leg? The
// per-market question is answered by GRADABLE_MARKETS_BY_LEAGUE below, for the
// leagues that opt in.
//
// Adding a league here is the ONE act that opens grading for it, on both client and
// server at once: calcStatus / isSettlementPendingForBet here, the staged-league gate
// in settlement-processor.js, the run buckets and secondary pass in settle-slips.js,
// and DQ CHECK 3/4. The PR that adds a league MUST, in the same PR: fill that
// adapter's RECOVERY_SWEEP_MARKETS (CC-IMPL-026) and, if the league needs a market
// allow-list, add its GRADABLE_MARKETS_BY_LEAGUE entry. 'nfl' joined in #1040 PR C.
// Frozen: two server consumers snapshot it into a Set at module load and two read it
// live (DQ CHECK 4 interpolates it into a PostgREST in.(...) filter), so a runtime
// mutation would split-brain the gate across files. Entries must be bare lowercase
// identifiers (pinned by settleable-leagues-drift.test.js).
export const SETTLEABLE_LEAGUES = Object.freeze(['nhl', 'mlb', 'nfl']);

// Per-league MARKET admission for calcStatus (#1040 PR C, GPT round-1 F3 / G3).
//
// The SETTLEABLE_LEAGUES gate above is league-level only, and the support table is
// not consulted by grading (see above: NHL `points`). So once a league is settleable,
// calcStatus's league-agnostic branches -- the moneyline score-diff branch, bare
// `spread` / `over_under`, and the `liveStatVal` branch that consults NEITHER league
// NOR capability -- would grade ANY market that arrives with a stat or a line. For
// NFL that meant `nfl:anytime_td` with a stale stat_value, `nfl:spread` with a line,
// or a bare `spread` all graded `won`. This map is the structural boundary: for a
// league listed here, calcStatus returns unresolvable (FINAL/OFF) / pending (else)
// for any market NOT in its Set, BEFORE any score/stat branch runs.
//
// A league with NO entry is ungated (NHL / MLB behaviour is byte-for-byte unchanged).
// The lookup key is the NORMALISED league (String(league).trim().toLowerCase()),
// exactly the key the SETTLEABLE_LEAGUES gate uses (G5) -- 'NFL' / ' nfl ' are nfl.
//
// Coupling pinned by settleable-leagues-drift.test.js: (members whose def is not
// `playerProp:false`) == that league's adapter RECOVERY_SWEEP_MARKETS as sets. A
// player-prop market admitted here but not swept can never receive frozen_stat.
//
// nfl:anytime_td admitted in #1353 PR 2 (reconciled scorer count, spec §6). NOT here for NFL:
// nfl:first_td / nfl:last_td (scoringPlays spike). nfl:spread / nfl:total admitted
// in PR D (team markets, playerProp:false -- excluded from the sweep-list rule,
// drift G3b/G3d).
//
// Object.freeze on a Set does not block Set#add; exact membership is pinned by test.
export const GRADABLE_MARKETS_BY_LEAGUE = Object.freeze({
  nfl: Object.freeze(new Set([
    'nfl:passing_yards',
    'nfl:rushing_yards',
    'nfl:receiving_yards',
    'nfl:receptions',
    'nfl:anytime_td',
    'nfl:moneyline',
    'nfl:total',
    'nfl:spread',
  ])),
});

export const SPORTSBOOKS = [
  {id:"fd",   name:"FanDuel",    color:"#1493ff", bg:"#001f3f", active:true},
  {id:"dk",   name:"DraftKings", color:"#53d337", bg:"#0a1f0a", active:true},
  {id:"mgm",  name:"BetMGM",     color:"#c9a84c", bg:"#1a1400", active:true},
  {id:"wh",   name:"William Hill",color:"#c9a84c", bg:"#0a1628", active:true},
  {id:"espn", name:"ESPN Bet",   color:"#d00", bg:"#1a0000",    active:false},
  {id:"fan",  name:"Fanatics",   color:"#e5e7eb", bg:"#0f172a", active:false},
];

// TEAM_BET_TYPES, PLAYER_PROP_TYPES, ALL_BET_TYPES removed —
// single source of truth is now src/lib/markets.js
// Use getDisplayMarketTypes('nhl', {category}) for the equivalent data.

export const NHL_FULL_NAMES = {
  ANA:"Anaheim Ducks", ARI:"Arizona Coyotes", BOS:"Boston Bruins",
  BUF:"Buffalo Sabres", CGY:"Calgary Flames", CAR:"Carolina Hurricanes",
  CHI:"Chicago Blackhawks", COL:"Colorado Avalanche", CBJ:"Columbus Blue Jackets",
  DAL:"Dallas Stars", DET:"Detroit Red Wings", EDM:"Edmonton Oilers",
  FLA:"Florida Panthers", LAK:"Los Angeles Kings", MIN:"Minnesota Wild",
  MTL:"Montréal Canadiens", NSH:"Nashville Predators", NJD:"New Jersey Devils",
  NYI:"New York Islanders", NYR:"New York Rangers", OTT:"Ottawa Senators",
  PHI:"Philadelphia Flyers", PIT:"Pittsburgh Penguins", STL:"St. Louis Blues",
  SJS:"San Jose Sharks", SEA:"Seattle Kraken", TBL:"Tampa Bay Lightning",
  TOR:"Toronto Maple Leafs", UTA:"Utah Hockey Club", VAN:"Vancouver Canucks",
  VGK:"Vegas Golden Knights", WSH:"Washington Capitals", WPG:"Winnipeg Jets",
};
